import {
  AppBadgeForAno,
  AppBadgeForID,
  AppLoadingSpinner,
  AppSectionTitle,
  AppTab,
  AppTabContent,
  AppTabList,
  AppTabTrigger,
} from '@9b3kuq5phwuk4txw/kiban-react-ui'
import { mdiMicrosoftTeams } from '@mdi/js'
import { INQUIRY_COMPLETED_STATUS_IDS } from 'shared'
import { useAnoMenuItems } from '@/hooks/useAnoMenuItems'
import { useUnsavedChangesGuard } from '../../-hooks/useUnsavedChangesGuard'
import { Route } from '../../index'
import { useInquiries } from '../ListArea/hooks/useInquiries'
import { UnsavedChangesConfirmDialog } from '../UnsavedChangesConfirmDialog'
import { AssignTab } from './components/AssignTab'
import { CoordinationTab } from './components/CoordinationTab'
import { LogsTab } from './components/LogsTab'
import { PropertiesTab } from './components/PropertiesTab'
import { SalesTab } from './components/SalesTab'

const GUARDED_TABS = ['properties', 'assign', 'coordination'] as const

export function DetailArea() {
  const { selectedId } = Route.useSearch()
  const { data, isLoading, isError } = useInquiries()

  const { currentTab, handleDirtyChange, handleTabChange, isDialogOpen, handleDiscard, handleDialogOpenChange } =
    useUnsavedChangesGuard('properties', GUARDED_TABS)

  const inquiry = selectedId ? data?.inquiries.find((item) => item.id === selectedId) : undefined
  const isInquiryCompleted = inquiry ? INQUIRY_COMPLETED_STATUS_IDS.has(inquiry.inquiry_assign_status_id) : false
  const anoMenuUrls = useAnoMenuItems({
    ano: inquiry?.ano ?? null,
    salesforceProjectId: inquiry?.salesforce_project_id,
  })

  if (isLoading) {
    return (
      <div className='flex h-full w-full items-center justify-center'>
        <AppLoadingSpinner />
      </div>
    )
  }

  if (isError) {
    return (
      <div className='flex h-full w-full items-center justify-center text-destructive'>
        <p>データの取得に失敗しました</p>
      </div>
    )
  }

  if (!inquiry) {
    return (
      <div className='flex h-full w-full items-center justify-center text-muted-foreground'>
        <p>一覧から引合を選択してください</p>
      </div>
    )
  }

  return (
    <div className='grid h-full w-full grid-rows-[auto_minmax(0,1fr)]'>
      {/* ヘッダー部 */}
      <AppSectionTitle>
        <div className='flex flex-col gap-3'>
          <div className='flex items-center justify-between text-xs'>
            <span className='text-muted-foreground'>
              引合ID: <AppBadgeForID value={inquiry.id} />
            </span>
            <span className='text-muted-foreground pr-4'>
              取引先責任者: <span className='font-bold text-foreground'>{inquiry.contact_name || '未設定'}</span>
            </span>
          </div>
          <div className='flex flex-col gap-2'>
            <div className='flex items-center gap-2 text-sm'>
              <span className='text-muted-foreground'>{inquiry.customer_name || '未設定'}</span>
              <span className='text-muted-foreground'>{inquiry.salesforce_project_name || '未設定'}</span>
            </div>
            <div className='flex items-center gap-2'>
              <span className='text-lg font-medium leading-tight'>{inquiry.deal_name || '未設定'}</span>
              <AppBadgeForAno ano={inquiry.ano} menuUrls={anoMenuUrls} />
              {inquiry.chat_id && (
                <a
                  href={`https://teams.microsoft.com/l/chat/${inquiry.chat_id}/conversations`}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='inline-flex items-center text-primary hover:text-primary/80'
                  aria-label='Teams'
                >
                  <svg className='h-4 w-4' viewBox='0 0 24 24' fill='currentColor'>
                    <title>Teams</title>
                    <path d={mdiMicrosoftTeams} />
                  </svg>
                </a>
              )}
            </div>
          </div>
        </div>
      </AppSectionTitle>

      {/* タブ部 */}
      <AppTab value={currentTab} onValueChange={handleTabChange} className='h-full min-w-0 overflow-hidden'>
        <AppTabList>
          <AppTabTrigger value='properties'>基本情報</AppTabTrigger>
          <AppTabTrigger value='assign'>アサイン</AppTabTrigger>
          <AppTabTrigger value='coordination'>調整状況</AppTabTrigger>
          <AppTabTrigger value='sales'>売上情報</AppTabTrigger>
          <AppTabTrigger value='logs'>変更履歴</AppTabTrigger>
        </AppTabList>

        <AppTabContent value='properties' className='flex-1 min-h-0'>
          <PropertiesTab
            key={inquiry.id}
            inquiry={inquiry}
            isInquiryCompleted={isInquiryCompleted}
            onDirtyChange={handleDirtyChange}
          />
        </AppTabContent>

        <AppTabContent value='assign' className='flex-1 min-h-0'>
          <AssignTab
            key={inquiry.id}
            inquiry={inquiry}
            isInquiryCompleted={isInquiryCompleted}
            onDirtyChange={handleDirtyChange}
          />
        </AppTabContent>

        <AppTabContent value='coordination' className='flex-1 min-h-0'>
          <CoordinationTab
            key={inquiry.id}
            inquiryId={inquiry.id}
            isInquiryCompleted={isInquiryCompleted}
            onDirtyChange={handleDirtyChange}
          />
        </AppTabContent>

        <AppTabContent value='sales' className='flex-1 min-h-0'>
          <SalesTab inquiryId={inquiry.id} />
        </AppTabContent>

        <AppTabContent value='logs' className='flex-1 min-h-0'>
          <LogsTab inquiryId={inquiry.id} />
        </AppTabContent>
      </AppTab>

      <UnsavedChangesConfirmDialog
        open={isDialogOpen}
        onOpenChange={handleDialogOpenChange}
        onDiscard={handleDiscard}
        {...(currentTab === 'coordination'
          ? { title: '入力中のコメントがあります', message: '送信していないコメントを破棄してよろしいですか？' }
          : { title: '変更が保存されていません', message: '保存されていない変更を破棄してよろしいですか？' })}
      />
    </div>
  )
}
